import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
}

interface DialogSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DialogSelectOption[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  allowClear?: boolean;
  clearLabel?: string;
  dropdownPosition?: "auto" | "top" | "bottom";
}

export function DialogSelect({
  value,
  onChange,
  options,
  placeholder = "هەڵبژێرە",
  searchable = false,
  searchPlaceholder = "گەڕان...",
  emptyMessage = "هیچ ئەنجامێک نەدۆزرایەوە",
  className,
  allowClear = false,
  clearLabel = "هیچی",
  dropdownPosition = "auto",
}: DialogSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [openUpward, setOpenUpward] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const isTouchRef = useRef(false);
  const shouldPreventRef = useRef(false);

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = searchable
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  // داخستنی dropdown کاتێک کلیک لە دەرەوە دەکرێت
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // فۆکەس بکە سەر input ی گەڕان
      if (searchable && searchInputRef.current) {
        setTimeout(() => searchInputRef.current?.focus(), 10);
      }
      
      // پشکنینی شوێن بۆ دیاریکردنی ئایا لە سەرەوە یان ژێرەوە دەربکەوێت
      if (dropdownPosition === "auto" && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const dropdownHeight = 250; // تەخمینی بەرزی dropdown
        
        setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
      } else if (dropdownPosition === "top") {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, searchable, dropdownPosition]);

  // داخستن بە Escape
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onTouchStart={(e) => {
          const touch = e.touches[0];
          touchStartRef.current = { x: touch.clientX, y: touch.clientY };
          isTouchRef.current = true;
          shouldPreventRef.current = false;
        }}
        onTouchMove={(e) => {
          if (touchStartRef.current) {
            const touch = e.touches[0];
            const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
            const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
            if (deltaX > 8 || deltaY > 8) {
              shouldPreventRef.current = true;
            }
          }
        }}
        onTouchEnd={(e) => {
          // ڕێگری لە click event چونکە لێرە handle دەکەین
          e.preventDefault();
          
          // تەنها دوای هەڵگرتنی دەست کارەکە بکە
          if (!shouldPreventRef.current) {
            setIsOpen(!isOpen);
          }
          
          // ڕیسێت
          shouldPreventRef.current = false;
          touchStartRef.current = null;
          
          // دوای کەمێک isTouchRef ڕیسێت بکە
          setTimeout(() => {
            isTouchRef.current = false;
          }, 100);
        }}
        onClick={() => {
          // تەنها بۆ mouse/keyboard - نەک touch
          if (!isTouchRef.current) {
            setIsOpen(!isOpen);
          }
        }}
        className={cn(
          "w-full h-10 px-3 text-sm bg-background border rounded-md",
          "flex items-center justify-between",
          "hover:bg-muted/50 transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-ring",
          isOpen && "ring-2 ring-ring"
        )}
      >
        <span className={!selectedOption ? "text-muted-foreground" : ""}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div
          className={cn(
            "absolute left-0 right-0 bg-background border rounded-md shadow-lg overflow-hidden",
            openUpward ? "bottom-full mb-1" : "top-full mt-1"
          )}
          style={{ zIndex: 999999 }}
        >
          {/* Search Input */}
          {searchable && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  className="w-full h-9 pr-8 pl-3 text-sm bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
          )}

          {/* Options List */}
          <div className="max-h-[200px] overflow-y-auto p-1">
            {/* Clear Option */}
            {allowClear && value && (
              <div
                className="flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent border-b mb-1"
                onClick={() => handleSelect("")}
              >
                <Check className="h-4 w-4 opacity-0" />
                <span className="text-muted-foreground">{clearLabel}</span>
              </div>
            )}

            {/* Options */}
            {filteredOptions.length === 0 ? (
              <div className="text-center py-4 text-sm text-muted-foreground">
                {emptyMessage}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <div
                  key={option.value}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer hover:bg-accent",
                    value === option.value && "bg-accent",
                    option.disabled && "opacity-50 cursor-not-allowed"
                  )}
                  onClick={() => {
                    if (!option.disabled) {
                      handleSelect(option.value);
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex-1">
                    <div>{option.label}</div>
                    {option.description && (
                      <div className="text-xs text-muted-foreground">
                        {option.description}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
